// src/Component/Features.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, IndianRupee, Zap, Sliders, 
  Coins, Landmark, PhoneCall, UserCheck,
  ShieldCheck, Cpu, Gauge, TrendingUp, History
} from 'lucide-react';

const Features: React.FC = () => {
  // PERFECT "TIGHT ZIG-ZAG" GRAPH DATA (Upward Bullish trend)
  // Rise by 20, drop by 10, rise by 20... creates a beautiful upward channel!
  const graphNodes = [
    { title: "Trade in BSE/NSE", icon: <Building2 size={24}/>, x: 5, y: 90, trend: 'up', pos: 'bottom' },
    { title: "AMC Charges - ₹0", icon: <IndianRupee size={24}/>, x: 13, y: 70, trend: 'up', pos: 'top' },
    { title: "Account Opening - ₹0", icon: <History size={24}/>, x: 21, y: 80, trend: 'down', pos: 'bottom' },
    { title: "No Password Sharing", icon: <ShieldCheck size={24}/>, x: 29, y: 60, trend: 'up', pos: 'top' },
    { title: "4X Intraday Limit", icon: <Zap size={24}/>, x: 37, y: 70, trend: 'down', pos: 'bottom' },
    { title: "Flexible Brokerage", icon: <Sliders size={24}/>, x: 45, y: 50, trend: 'up', pos: 'top' },
    { title: "Stop Loss Circuit", icon: <Gauge size={24}/>, x: 53, y: 60, trend: 'down', pos: 'bottom' },
    { title: "No Manual Handling", icon: <Cpu size={24}/>, x: 61, y: 40, trend: 'up', pos: 'top' },
    { title: "Average 30% ROI", icon: <TrendingUp size={24}/>, x: 69, y: 50, trend: 'down', pos: 'bottom' },
    { title: "12X Cash Limit", icon: <Coins size={24}/>, x: 77, y: 30, trend: 'up', pos: 'top' },
    { title: "Margin Funding", icon: <Landmark size={24}/>, x: 85, y: 40, trend: 'down', pos: 'bottom' },
    { title: "Call & Trade Support", icon: <PhoneCall size={24}/>, x: 93, y: 20, trend: 'up', pos: 'top' },
  ];

  const pathD = graphNodes.map((n, i) => `${i === 0 ? 'M' : 'L'} ${n.x} ${n.y}`).join(' ');

  return (
    <section className="relative bg-slate-50 py-16 md:py-24 font-[Urbanist] overflow-hidden">
      
      <div 
        className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" 
        style={{ 
          backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', 
          backgroundSize: '40px 40px' 
        }} 
      />

      <div className="container mx-auto px-4 sm:px-6 relative z-10 mb-8">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center space-y-4"
        >
          <span className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-blue-100 text-blue-700 text-sm font-bold tracking-wide border border-blue-200 uppercase">
            Why Choose Us
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
            Advantage <span className="text-red-700">Motilal Algo</span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto font-semibold md:text-xl">
            Experience the most powerful trading features designed for Indian Traders.
          </p>
        </motion.div>
      </div>

      {/* ========================================== */}
      {/* DESKTOP VERSION (TIGHT ZIG-ZAG GRAPH) */}
      {/* ========================================== */}
      <div className="hidden md:block w-full max-w-[1500px] mx-auto relative z-10 px-8">
        <div className="w-full h-[550px] relative mt-16">
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" viewBox="0 0 100 100" preserveAspectRatio="none">
            <motion.path 
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 2.5, ease: "easeInOut" }}
              d={pathD} stroke="#cbd5e1" strokeWidth="0.3" fill="none" 
            />
          </svg>

          {graphNodes.map((node, index) => {
            const isUp = node.trend === 'up';
            const colorClass = isUp ? 'text-green-600 bg-green-50 border-green-200' : 'text-red-600 bg-red-50 border-red-200';
            const candleColor = isUp ? 'bg-green-500' : 'bg-red-500';

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, type: "spring", bounce: 0.5 }}
                className="absolute flex justify-center items-center group z-10"
                style={{ left: `${node.x}%`, top: `${node.y}%` }}
              >
                {/* SVG Candle Stick Design */}
                <div className="relative flex justify-center items-center z-20">
                  <div className={`absolute w-[2px] h-12 ${candleColor} opacity-50`} />
                  <div className={`w-4 h-8 ${candleColor} rounded-[2px] border border-white z-10 shadow-lg`} />
                </div>

                {/* Info Card */}
                <div className={`absolute w-52 bg-white border ${colorClass} rounded-2xl p-4 text-center shadow-xl group-hover:-translate-y-2 transition-transform duration-300 ${
                  node.pos === 'top' ? 'bottom-12' : 'top-12'
                }`}>
                  <div className="mb-2 flex justify-center">{node.icon}</div>
                  <p className="text-slate-900 text-base font-black leading-tight tracking-tight">
                    {node.title}
                  </p>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>

      {/* ========================================== */}
      {/* MOBILE VERSION (SLIDE-IN ANIMATED LIST) */}
      {/* ========================================== */}
      <div className="md:hidden flex flex-col items-center px-4 relative z-10 mt-6 pb-10 overflow-hidden">
        
        {/* Animated Center Line */}
        <motion.div 
          initial={{ height: 0 }}
          whileInView={{ height: 'calc(100% - 60px)' }}
          viewport={{ once: true }}
          transition={{ duration: 2, ease: "easeInOut" }}
          className="absolute left-1/2 -translate-x-1/2 top-10 w-[2px] bg-slate-200 z-0" 
        />

        {graphNodes.map((node, index) => {
          const isUp = node.trend === 'up';
          const candleColor = isUp ? 'bg-green-500' : 'bg-red-500';
          const iconBg = isUp ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600';

          return (
            <motion.div 
              key={index}
              // Alternating left/right slide in for mobile
              initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60, scale: 0.8 }}
              whileInView={{ opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6, type: "spring", bounce: 0.4 }}
              className="flex flex-col items-center relative w-full max-w-[340px]"
            >
              {/* Center Linker */}
              <div className="h-14 flex items-center justify-center relative z-10 w-full">
                <div className={`absolute w-[2px] h-full ${candleColor} opacity-40`} />
                <div className={`w-3.5 h-8 ${candleColor} rounded-[2px] border border-white shadow-md z-10`} />
              </div>
              
              {/* Feature Card */}
              <div className="w-full bg-white border-2 border-slate-100 p-4 rounded-2xl flex items-center gap-4 shadow-md z-20 relative hover:border-slate-300 transition-all">
                <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}>
                  {React.cloneElement(node.icon as React.ReactElement)}
                </div>
                <span className="text-slate-900 text-[17px] font-black leading-none tracking-tight flex-1">
                  {node.title}
                </span>
              </div>
            </motion.div>
          )
        })}
      </div>
    </section>
  );
};

export default Features;